import { CatalogueListProps } from './CatalogueList.types'
import { ItemStatus } from '../../../interfaces/enums'
import {
  CatalogueDefault,
  CatalogueItemList,
  CatalogueListWrapper,
  CatalogCategorySpinner,
  LoadingCategoryText,
  CatalogueListObserver
} from './CatalogueList.styles'
import Message from '../Message'
import useCategoryObserver from './useCategoryObserver'
import useId from '../../hooks/useId'
import Icon from '../Icon'

const CatalogueList = ({
  id,
  className = '',
  styles,
  catalogue = [],
  groupsGap,
  heading = { buttons: [] },
  status = ItemStatus.OK,
  initCategory = '',
  emptyMessage,
  errorMessage,
  loadingMessage = {
    loader: true
  },
  isFetchingMore = false,
  loadingMoreElementsText = '',
  categoryEnterActions,
  'data-testid': testid = 'catalogue',
  showAllCategoriesButton = false,
  showAllCategoriesText,
  showAllCategoriesAction,
  viewObserver,
  hidden = false,
  ...props
}: CatalogueListProps) => {
  const { getId } = useId(id)
  const { getTestId } = useId(testid)
  const categoriesRef = useCategoryObserver(
    catalogue,
    categoryEnterActions,
    initCategory
  )

  if (showAllCategoriesButton) {
    heading?.buttons?.push({
      label: showAllCategoriesText || '',
      endIcon: <Icon name="CmSquareArrowUpRight" iconset="cm" />,
      actions: showAllCategoriesAction
    })
  }

  if (hidden) return null

  return (
    <>
      {!!catalogue?.length && status !== ItemStatus.LOADING && (
        <CatalogueListWrapper
          id={getId()}
          className={`CatalogueList ${className}`}
          gap={groupsGap}
          sx={styles}
          data-testid={getTestId()}>
          <CatalogueDefault
            id="cat-all"
            data-category="all"
            ref={categoriesRef.all}
          />
          {catalogue?.map((categoryCatalog) => {
            const {
              category = '',
              slug = '',
              productsPrices,
              parentId
            } = categoryCatalog
            const headingProps = {
              value: category,
              ...heading
            }
            // only show empty message if there are no products and does it has parentId
            const shouldShowEmptyMessage: boolean =
              !productsPrices?.length && status === ItemStatus.OK && !!parentId
            return (
              <div
                key={slug}
                ref={categoriesRef[slug]}
                id={getId(slug)}
                data-category={slug}>
                <CatalogueItemList
                  items={productsPrices}
                  heading={headingProps}
                  data-testid={getTestId(slug)}
                  shouldShowEmptyMessage={shouldShowEmptyMessage}
                  {...props}
                />
                <div data-category-name={category} />
                {isFetchingMore && (
                  <>
                    <CatalogCategorySpinner
                      data-testid={getTestId('loading-spinner')}
                    />
                    <LoadingCategoryText
                      data-testid={getTestId('loading-text')}>
                      {' '}
                      {loadingMoreElementsText}{' '}
                    </LoadingCategoryText>
                  </>
                )}
                {!!viewObserver && (
                  <CatalogueListObserver
                    {...viewObserver}
                    loading={isFetchingMore}
                    data={categoryCatalog}
                    data-testid={getTestId('catalogue-category-observer')}
                  />
                )}
              </div>
            )
          })}
        </CatalogueListWrapper>
      )}
      {!catalogue?.length && status === ItemStatus.OK && emptyMessage && (
        <Message
          id={getId('empty-message')}
          data-testid={getTestId('empty-message')}
          {...emptyMessage}
        />
      )}
      {!catalogue?.length && status === ItemStatus.ERROR && errorMessage && (
        <Message
          id={getId('error-message')}
          data-testid={getTestId('error-message')}
          {...errorMessage}
        />
      )}
      {status === ItemStatus.LOADING && (
        <Message
          id={getId('loading-message')}
          data-testid={getTestId('loading-message')}
          {...loadingMessage}
        />
      )}
    </>
  )
}

export default CatalogueList
